/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Active purchases or the remaining purchase offers. */
export type PurchaseOfferSegment =
  /** Purchases in progress, including backup offers. */
  | 'ACTIVE'
  /** Remaining purchases, including ended and completed offers. */
  | 'OTHER';

/** Lifecycle state of a buyer purchase offer. */
export type PurchaseOfferState =
  /** Offer is active. */
  | 'ACTIVE'
  /** Offer is under attorney review. */
  | 'ATTORNEY_REVIEW'
  /** Offer awaits the buyer’s signature. */
  | 'AWAITING_CUSTOMER_SIGNATURE'
  /** The backup offer expired after the property sold or became unavailable. */
  | 'BACKUP_EXPIRED'
  /** Offer is a backup offer. */
  | 'BACKUP_OFFER'
  /** Buyer withdrew from the offer. */
  | 'BUYER_WALKED'
  /** Offer was canceled. */
  | 'CANCELED'
  /** Purchase was completed and closed. */
  | 'CLOSED'
  /** Offer contingencies have been released. */
  | 'CONTINGENCIES_RELEASED'
  /** The purchase contract fell through. */
  | 'CONTRACT_FELL_THROUGH'
  /** A counteroffer is pending. */
  | 'COUNTER_OFFER_PENDING'
  /** Offer is under contract. */
  | 'IN_CONTRACT'
  /** Offer is being negotiated. */
  | 'IN_NEGOTIATION'
  /** Offer was rejected. */
  | 'REJECTED'
  /** Offer status is not recognized. */
  | 'UNKNOWN';

/** Purchase workflow phase represented by a task group. */
export type PurchaseOfferTaskGroupPhase =
  /** Tasks for escrow and closing. */
  | 'ESCROW_CLOSING'
  /** Tasks for reviewing and completing the offer. */
  | 'OFFER'
  /** Tasks for receiving the home after closing. */
  | 'WELCOME_HOME';

/** Completion state of a purchase offer task. */
export type PurchaseOfferTaskState =
  /** Task is complete. */
  | 'COMPLETE'
  /** Task still needs attention. */
  | 'PENDING';

export type HubPurchasePageQueryVariables = Exact<{
  segment: Types.PurchaseOfferSegment;
  search?: Types.Scalars['String']['input'] | null | undefined;
  after?: Types.Scalars['String']['input'] | null | undefined;
  includeHomeSummary?: Types.Scalars['Boolean']['input'];
}>;


export type HubPurchasePageQuery = { customerAccount: { buyer: { purchaseOffers: { totalCount: Types.Scalars['SafeInt']['output'], activeCount: Types.Scalars['SafeInt']['output'], otherCount: Types.Scalars['SafeInt']['output'], pageInfo: { endCursor: Types.Scalars['String']['output'] | null, hasNextPage: Types.Scalars['Boolean']['output'] }, rows: Array<{ id: Types.Scalars['ID']['output'], isHidden: Types.Scalars['Boolean']['output'], clients: Array<{ name: Types.Scalars['String']['output'] }>, offer: { id: Types.Scalars['ID']['output'], transactionState: Types.PurchaseOfferState, submittedAt: Types.Scalars['DateTime']['output'] | null, closeOfEscrowDate: Types.Scalars['Date']['output'] | null, priceCents: Types.Scalars['SafeInt']['output'] | null, listing: { marketplaceListing: { addressId: Types.Scalars['UUID']['output'], street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']> } | null } | null, taskGroups: Array<{ title: Types.Scalars['NonEmptyString']['output'], phase: Types.PurchaseOfferTaskGroupPhase, progress: { completed: Types.Scalars['SafeInt']['output'], total: Types.Scalars['SafeInt']['output'] } | null, tasks: Array<
                | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
                | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
              > }> } | null }>, homeSummary?: { homeCount: Types.Scalars['SafeInt']['output'], rows: Array<{ id: Types.Scalars['ID']['output'], clients: Array<{ name: Types.Scalars['String']['output'] }>, offer: { id: Types.Scalars['ID']['output'], transactionState: Types.PurchaseOfferState, submittedAt: Types.Scalars['DateTime']['output'] | null, closeOfEscrowDate: Types.Scalars['Date']['output'] | null, priceCents: Types.Scalars['SafeInt']['output'] | null, listing: { marketplaceListing: { addressId: Types.Scalars['UUID']['output'], street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']> } | null } | null, taskGroups: Array<{ title: Types.Scalars['NonEmptyString']['output'], phase: Types.PurchaseOfferTaskGroupPhase, progress: { completed: Types.Scalars['SafeInt']['output'], total: Types.Scalars['SafeInt']['output'] } | null, tasks: Array<
                  | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
                  | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
                > }> } | null }> } | null } } } };

export type HubPurchasePageOfferFragment = { id: Types.Scalars['ID']['output'], transactionState: Types.PurchaseOfferState, submittedAt: Types.Scalars['DateTime']['output'] | null, closeOfEscrowDate: Types.Scalars['Date']['output'] | null, priceCents: Types.Scalars['SafeInt']['output'] | null, listing: { marketplaceListing: { addressId: Types.Scalars['UUID']['output'], street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']> } | null } | null, taskGroups: Array<{ title: Types.Scalars['NonEmptyString']['output'], phase: Types.PurchaseOfferTaskGroupPhase, progress: { completed: Types.Scalars['SafeInt']['output'], total: Types.Scalars['SafeInt']['output'] } | null, tasks: Array<
      | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
      | { title: Types.Scalars['NonEmptyString']['output'], state: Types.PurchaseOfferTaskState | null }
    > }> };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}
export const HubPurchasePageOfferFragmentDoc = /*#__PURE__*/ new TypedDocumentString(`
    fragment HubPurchasePageOffer on PurchaseOffer {
  id
  transactionState
  submittedAt
  closeOfEscrowDate
  priceCents
  listing {
    marketplaceListing {
      addressId
      street
      city
      state
      postalCode
      bedrooms
      bathrooms
      totalLivingSquareFootage
      photoUrls(first: 1)
    }
  }
  taskGroups {
    title
    phase
    progress {
      completed
      total
    }
    tasks {
      title
      state
    }
  }
}
    `, {"fragmentName":"HubPurchasePageOffer"}) as unknown as TypedDocumentString<HubPurchasePageOfferFragment, unknown>;
export const HubPurchasePageDocument = /*#__PURE__*/ new TypedDocumentString(`
    query HubPurchasePage($segment: PurchaseOfferSegment!, $search: String, $after: String, $includeHomeSummary: Boolean! = false) {
  customerAccount {
    buyer {
      purchaseOffers(segment: $segment, search: $search, first: 5, after: $after) {
        totalCount
        activeCount
        otherCount
        pageInfo {
          endCursor
          hasNextPage
        }
        rows {
          id
          isHidden
          clients {
            name
          }
          offer {
            ...HubPurchasePageOffer
          }
        }
        homeSummary @include(if: $includeHomeSummary) {
          homeCount
          rows {
            id
            clients {
              name
            }
            offer {
              ...HubPurchasePageOffer
            }
          }
        }
      }
    }
  }
}
    fragment HubPurchasePageOffer on PurchaseOffer {
  id
  transactionState
  submittedAt
  closeOfEscrowDate
  priceCents
  listing {
    marketplaceListing {
      addressId
      street
      city
      state
      postalCode
      bedrooms
      bathrooms
      totalLivingSquareFootage
      photoUrls(first: 1)
    }
  }
  taskGroups {
    title
    phase
    progress {
      completed
      total
    }
    tasks {
      title
      state
    }
  }
}`) as unknown as TypedDocumentString<HubPurchasePageQuery, HubPurchasePageQueryVariables>;